import { useState, useEffect } from "react";
import { useApp, api } from "../context";
import { Shell, EmptyState } from "../components";
import { peekDeepLink, consumeDeepLink, buildEventUrl } from "../deeplink";

// ── HELPERS ──────────────────────────────────────────────────────────────────
const PRI = "#6c5ce7";
const PRI2 = "#a23ad1";
const GRN = "#10b981";

const fmtDate = (d) => {
  const dt = new Date(d);
  return dt.toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
};
const fmtDay = (d) => new Date(d).getDate();
const fmtMonth = (d) => new Date(d).toLocaleString("en", { month: "short" }).toUpperCase();
const fmtWeekday = (d) => new Date(d).toLocaleDateString("en", { weekday: "long" });
const fmtFullDate = (d) => new Date(d).toLocaleDateString("en", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
const daysLeft = (d) => Math.ceil((new Date(d) - new Date()) / 86400000);

const WhoComing = ({ data }) => {
  if (!data) return null;
  const items = [
    { label: "Gays", color: PRI, pct: data.gays || 0 },
    { label: "Lesbians", color: "#f59e0b", pct: data.lesbians || 0 },
    { label: "Bisexuals", color: "#ec4899", pct: data.bisexuals || 0 },
    { label: "Straight", color: "#3b82f6", pct: data.straight || 0 },
    { label: "Others", color: "#9ca3af", pct: data.others || 0 },
  ];
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
      {items.map(it => (
        <div key={it.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <div style={{ width: 10, height: 10, borderRadius: 5, background: it.color }} />
          <div style={{ fontSize: 10, fontWeight: 700, color: "#333" }}>{it.label}</div>
          <div style={{ fontSize: 10, color: it.color, fontWeight: 700 }}>{it.pct}%</div>
        </div>
      ))}
    </div>
  );
};

// ── UPCOMING EVENT CARD ───────────────────────────────────────────────────────
const UpcomingCard = ({ ev, myTicket, onViewDetail }) => {
  const { c } = useApp();
  const dl = daysLeft(ev.event_date);
  const attending = ev.tickets_count || 0;
  return (
    <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, marginBottom: 12, overflow: "hidden", boxShadow: c.shd }}>
      <div style={{ display: "flex", gap: 0 }}>
        {/* Left — Square Image */}
        <div style={{ width: 130, minHeight: 180, flexShrink: 0, background: ev.image_url ? `url(${ev.image_url}) center/cover` : `linear-gradient(135deg, ${PRI}, ${PRI2})`, borderRadius: "16px 0 0 0" }} />
        {/* Right — Info */}
        <div style={{ flex: 1, padding: "12px 12px 10px", minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: PRI, marginBottom: 3 }}>{fmtDate(ev.event_date)}</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: c.text, lineHeight: 1.2, marginBottom: 3 }}>{ev.title}</div>
          <div style={{ fontSize: 11, color: c.sub, marginBottom: 6, lineHeight: 1.4 }}>{ev.description ? ev.description.slice(0, 60) + "..." : ""}</div>
          <div style={{ fontSize: 11, color: c.sub, marginBottom: 2 }}>📍 {ev.venue}</div>
          <div style={{ fontSize: 11, color: c.sub, marginBottom: 2 }}>🕐 {ev.event_time || "TBA"}{ev.time_end ? ` – ${ev.time_end}` : ""}</div>
          <div style={{ fontSize: 11, color: c.sub, marginBottom: 8 }}>👥 {attending} attending</div>
          <button onClick={() => onViewDetail(ev)} style={{ padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${PRI}`, background: "transparent", color: PRI, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            View Details ›
          </button>
        </div>
      </div>
      {/* Bottom — Who's coming */}
      {ev.whos_coming && (
        <div style={{ padding: "10px 14px 12px", borderTop: `1px solid ${c.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: c.sub, marginBottom: 6 }}>Who's coming:</div>
              <WhoComing data={ev.whos_coming} />
            </div>
            {ev.age_min && ev.age_max && (
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, color: c.sub, fontWeight: 600 }}>Age range:</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: PRI }}>{ev.age_min} – {ev.age_max}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── MY TICKET CARD ────────────────────────────────────────────────────────────
const MyTicketCard = ({ ticket, onView }) => {
  const { c } = useApp();
  const ev = ticket.event || {};
  const tt = ticket.ticket_type || ticket.ticketType || {};
  return (
    <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, marginBottom: 12, overflow: "hidden", boxShadow: c.shd }}>
      <div style={{ display: "flex" }}>
        <div style={{ width: 120, minHeight: 160, flexShrink: 0, background: ev.image_url ? `url(${ev.image_url}) center/cover` : `linear-gradient(135deg, ${PRI}, ${PRI2})`, borderRadius: "16px 0 0 0" }} />
        <div style={{ flex: 1, padding: "12px 12px 10px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: PRI, marginBottom: 3 }}>{ev.event_date ? fmtDate(ev.event_date) : ""}</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: c.text, marginBottom: 4 }}>{ev.title || "Event"}</div>
          <div style={{ fontSize: 11, color: c.sub, marginBottom: 2 }}>🕐 {ev.event_time || "TBA"}{ev.time_end ? ` – ${ev.time_end}` : ""}</div>
          <div style={{ fontSize: 11, color: c.sub, marginBottom: 8 }}>📍 {ev.venue || ""}</div>
          <div style={{ fontSize: 11, color: c.sub, marginBottom: 8 }}>{tt.name || "General Admission"}</div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, border: `1px solid ${PRI}`, marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: PRI, fontWeight: 700 }}>🎫 {ticket.quantity || 1} Ticket{(ticket.quantity || 1) > 1 ? "s" : ""}</span>
          </div>
        </div>
      </div>
      <button onClick={() => onView(ticket)} style={{ width: "100%", padding: "13px 0", border: "none", background: `linear-gradient(135deg, ${PRI}, ${PRI2})`, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
        View Ticket <span style={{ fontSize: 16 }}>›</span>
      </button>
    </div>
  );
};

// ── PAST EVENT CARD ───────────────────────────────────────────────────────────
const PastCard = ({ ev, attended, onViewDetail }) => {
  const { c } = useApp();
  return (
    <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, marginBottom: 12, overflow: "hidden", boxShadow: c.shd }}>
      <div style={{ display: "flex" }}>
        <div style={{ width: 120, minHeight: 160, flexShrink: 0, background: ev.image_url ? `url(${ev.image_url}) center/cover` : `linear-gradient(135deg, ${PRI}, ${PRI2})`, borderRadius: "16px 0 0 0" }} />
        <div style={{ flex: 1, padding: "12px 12px 10px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: PRI, marginBottom: 3 }}>{fmtDate(ev.event_date)}</div>
          <div style={{ fontSize: 15, fontWeight: 800, color: c.text, marginBottom: 4 }}>{ev.title}</div>
          <div style={{ fontSize: 11, color: c.sub, marginBottom: 2 }}>🕐 {ev.event_time || "TBA"}{ev.time_end ? ` – ${ev.time_end}` : ""}</div>
          <div style={{ fontSize: 11, color: c.sub, marginBottom: 10 }}>📍 {ev.venue}</div>
          {attended && (
            <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, border: `1.5px solid ${GRN}`, background: `${GRN}10` }}>
              <span style={{ fontSize: 11, color: GRN, fontWeight: 700 }}>✅ Attended</span>
            </div>
          )}
        </div>
      </div>
      <button onClick={() => onViewDetail(ev)} style={{ width: "100%", padding: "12px 0", border: `1.5px solid ${PRI}`, background: "transparent", color: PRI, fontSize: 13, fontWeight: 700, cursor: "pointer", borderRadius: "0 0 16px 16px" }}>
        View Details
      </button>
    </div>
  );
};

// ── EVENT SHARE SHEET ─────────────────────────────────────────────────────────
const EventShareSheet = ({ ev, onClose }) => {
  const { dark, flash } = useApp();
  const url = buildEventUrl(ev.id);
  const blurb = `${ev.title} — ${fmtFullDate(ev.event_date)} at ${ev.venue}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); flash("Event link copied! 🔗"); }
    catch { flash("Copy failed — long-press the link below", "error"); }
    onClose();
  };
  const whatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${blurb}\n\nGet your ticket:\n${url}`)}`, "_blank", "noopener,noreferrer");
    onClose();
  };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: ev.title, text: blurb, url }); } catch { /* dismissed */ } }
    else { try { await navigator.clipboard.writeText(url); flash("Link copied! 🔗"); } catch { /* ignore */ } }
    onClose();
  };

  const items = [
    { icon: "🔗", label: "Copy Link", desc: "Opens straight to this event", color: PRI, onClick: copyLink },
    { icon: "📲", label: "Share on WhatsApp", desc: "Send to contacts or your status", color: "#25D366", onClick: whatsApp },
    { icon: "↗️", label: "Share to other apps", desc: "Use your device share menu", color: "#3b82f6", onClick: nativeShare },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9998, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={onClose}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} />
      <div onClick={e => e.stopPropagation()} style={{ position: "relative", background: dark ? "#1a1b2e" : "#fff", borderRadius: "20px 20px 0 0", padding: "8px 0 40px", width: "100%", maxWidth: 480, zIndex: 1 }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: "#ffffff20", margin: "8px auto 16px" }} />
        <div style={{ padding: "0 20px 14px", fontSize: 14, fontWeight: 700, color: dark ? "#e4e6f0" : "#1a1c2e" }}>Share Event</div>
        {items.map((item, i) => (
          <button key={i} onClick={item.onClick} style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "12px 20px", border: "none", background: "transparent", cursor: "pointer", textAlign: "left" }}>
            <div style={{ width: 40, height: 40, borderRadius: 12, background: `${item.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{item.icon}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: dark ? "#e4e6f0" : "#1a1c2e" }}>{item.label}</div>
              <div style={{ fontSize: 11, color: "#8890b0", marginTop: 1 }}>{item.desc}</div>
            </div>
          </button>
        ))}
        <div style={{ padding: "10px 20px 0", fontSize: 11, color: "#8890b0", wordBreak: "break-all" }}>{url}</div>
      </div>
    </div>
  );
};

// ── EVENT DETAIL SCREEN ───────────────────────────────────────────────────────
const EventDetail = ({ ev: evProp, myTicket: myTicketProp, onBack, onBuy, onViewTicket, onMyTickets }) => {
  const { c, dark, user, setUser, flash } = useApp();
  const [ev, setEv] = useState(evProp);
  const [myTicket, setMyTicket] = useState(myTicketProp);
  const [ticketTypes, setTicketTypes] = useState(evProp.ticket_types || evProp.ticketTypes || []);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [payTab, setPayTab] = useState("mpesa");
  const [selectedType, setSelectedType] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [phone, setPhone] = useState("");
  const [initialAmount, setInitialAmount] = useState("");
  const [paying, setPaying] = useState(false);
  const [wishlist, setWishlist] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [payState, setPayState] = useState(null);
  const [payError, setPayError] = useState("");
  const [paidMethod, setPaidMethod] = useState("");
  const [instOpen, setInstOpen] = useState(false);
  const [instPhone, setInstPhone] = useState("");
  const [instPaying, setInstPaying] = useState(false);
  const [instState, setInstState] = useState(null);
  const [instError, setInstError] = useState("");

  useEffect(() => {
    api(`/events/${evProp.id}`).then(r => {
      if (r.ok && r.data) {
        const fresh = r.data.event || r.data;
        setEv(fresh);
        setTicketTypes(fresh.ticket_types || fresh.ticketTypes || []);
        if (r.data.my_ticket) setMyTicket(r.data.my_ticket);
      }
      setLoadingDetail(false);
    });
  }, [evProp.id]); // eslint-disable-line

  const isPast = ev.status === "completed" || ev.status === "cancelled";
  const dl = daysLeft(ev.event_date);
  const canInstallment = dl >= 3;
  const totalAmount = selectedType ? selectedType.price * quantity : 0;
  const minDeposit = selectedType ? Math.ceil(totalAmount * 0.30) : 0;
  const tierColors = { "Early Bird": GRN, "Regular": PRI, "VIP": "#f59e0b", "VVIP": "#ef4444" };

  const handlePay = async () => {
    console.log("handlePay called, payTab:", payTab, "selectedType:", selectedType?.name);
    if (!selectedType) { flash("Select a ticket type", "error"); return; }
    setPayState("processing");
    setPaying(true);
    try {
      if (payTab === "mpesa") {
        if (!phone || phone.length < 9) { setPayState(null); setPaying(false); flash("Enter a valid phone number", "error"); return; }
        const r = await api(`/events/${ev.id}/buy-ticket`, { method: "POST", body: JSON.stringify({ ticket_type_id: selectedType.id, phone, quantity }) });
        if (r.ok) {
          setPaidMethod("mpesa");
          setPayState("waiting_pin");
          const ticketId = r.data?.ticket?.id;
          if (ticketId) {
            let attempts = 0;
            const poll = setInterval(async () => {
              attempts++;
              const s = await api(`/event-tickets/${ticketId}/status`);
              if (s.ok && s.data?.payment_status === "completed") {
                clearInterval(poll);
                setPayState("success");
              } else if (s.ok && s.data?.payment_status === "cancelled") {
                clearInterval(poll);
                setPayError("Payment was cancelled. Please try again.");
                setPayState("failed");
              } else if (attempts >= 24) {
                clearInterval(poll);
                setPayError("Payment timed out. Please try again.");
                setPayState("failed");
              }
            }, 5000);
          } else { setPayState("success"); }
        }
        else { setPayError(r.data?.message || "Payment failed. Check your M-Pesa balance or try a different number."); setPayState("failed"); }
      } else if (payTab === "tokens") {
        const r = await api(`/events/${ev.id}/buy-with-tokens`, { method: "POST", body: JSON.stringify({ ticket_type_id: selectedType.id, quantity }) });
        if (r.ok) { setPaidMethod("tokens"); setPayState("success"); setUser(u => ({ ...u, gtBalance: (u.gtBalance || 0) - totalAmount })); }
        else { setPayError(r.data?.message || "Insufficient GT Tokens. Please top up your wallet."); setPayState("failed"); }
      } else if (payTab === "install") {
        if (!phone || phone.replace(/\s/g,"").length < 9) { setPayState(null); setPaying(false); flash("Enter a valid phone number", "error"); return; }
        const amt = Math.ceil(totalAmount / 3);
        const r = await api(`/events/${ev.id}/start-installment`, { method: "POST", body: JSON.stringify({ ticket_type_id: selectedType.id, phone: phone.replace(/\s/g,""), quantity: parseInt(quantity), initial_amount: amt }) });
        if (r.ok) {
          setPaidMethod("install");
          setPayState("waiting_pin");
          const ticketId = r.data?.ticket?.id;
          if (ticketId) {
            let attempts = 0;
            const poll = setInterval(async () => {
              attempts++;
              const s = await api(`/event-tickets/${ticketId}/status`);
              if (s.ok && s.data?.payment_status === "completed") {
                clearInterval(poll);
                setPayState("success");
              } else if (s.ok && s.data?.payment_status === "cancelled") {
                clearInterval(poll);
                setPayError("Payment was cancelled. Please try again.");
                setPayState("failed");
              } else if (attempts >= 24) {
                clearInterval(poll);
                setPayError("Payment timed out. Please try again.");
                setPayState("failed");
              }
            }, 5000);
          } else { setPayState("success"); }
        }
        else { setPayError(r.data?.message || "Payment failed. Please try again."); setPayState("failed"); }
      }
    } finally { setPaying(false); }
  };

  const PayModal = () => {
    if (!payState) return null;
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ width: "100%", maxWidth: 340, background: c.card, borderRadius: 24, padding: 30, textAlign: "center", border: `1px solid ${c.border}` }}>
          {(payState === "processing" || payState === "waiting_pin") && (
            <>
              <div style={{ width: 80, height: 80, borderRadius: 40, background: "#e8fff0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, margin: "0 auto 20px" }}>📱</div>
              <div style={{ width: 50, height: 50, border: "4px solid #e5e7eb", borderTopColor: "#10b981", borderRadius: "50%", animation: "gcSpin 1s linear infinite", margin: "0 auto 16px" }} />
              <style>{`@keyframes gcSpin{to{transform:rotate(360deg)}}`}</style>
              <div style={{ fontSize: 18, fontWeight: 800, color: c.text, marginBottom: 8 }}>
                {payState === "waiting_pin" ? "Enter Your M-Pesa PIN" : "Processing Payment"}
              </div>
              <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.6 }}>
                {payState === "waiting_pin"
                  ? "Check your phone for the M-Pesa prompt. Enter your PIN — do not close this page."
                  : payTab === "tokens" ? "Deducting GT Tokens..." : "Sending M-Pesa prompt to your phone..."}
              </div>
              {payState === "waiting_pin" && (
                <div style={{ marginTop: 16, padding: "8px 16px", borderRadius: 10, background: "#f59e0b15", border: "1px solid #f59e0b30" }}>
                  <div style={{ fontSize: 11, color: "#f59e0b", fontWeight: 700 }}>⏳ Waiting for payment confirmation...</div>
                </div>
              )}
            </>
          )}
          {payState === "success" && (
            <>
              <div style={{ width: 80, height: 80, borderRadius: 40, background: "#e8fff0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, margin: "0 auto 20px" }}>🎉</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: c.text, marginBottom: 8 }}>{paidMethod === "tokens" ? "Tickets Confirmed!" : paidMethod === "install" ? "Deposit Initiated!" : "M-Pesa Prompt Sent!"}</div>
              <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.6, marginBottom: 20 }}>
                {paidMethod === "tokens" && "Your GT Tokens have been deducted. Ticket confirmed!"}
                {paidMethod === "install" && `Check your phone for the M-Pesa prompt. Payment 1 of 3 — KES ${Math.ceil(totalAmount / 3).toLocaleString()}.`}
                {paidMethod === "mpesa" && "Check your phone for the M-Pesa STK push. Enter your PIN to complete booking."}
              </div>
              <div style={{ background: c.bg, borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
                <div style={{ fontSize: 11, color: c.faint, marginBottom: 2 }}>Event</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 6 }}>{ev.title}</div>
                <div style={{ fontSize: 11, color: c.faint, marginBottom: 2 }}>Ticket</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 6 }}>{selectedType?.name} × {quantity}</div>
                <div style={{ fontSize: 11, color: c.faint, marginBottom: 2 }}>Amount</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: PRI }}>KES {totalAmount.toLocaleString()}</div>
              </div>
              <button onClick={() => { setPayState(null); onMyTickets(); }} style={{ width: "100%", padding: "14px 0", borderRadius: 14, border: "none", background: `linear-gradient(135deg,${PRI},${PRI2})`, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>🎫 View My Tickets</button>
            </>
          )}
          {payState === "failed" && (
            <>
              <div style={{ width: 80, height: 80, borderRadius: 40, background: "#fff0f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, margin: "0 auto 20px" }}>✕</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: c.text, marginBottom: 8 }}>Payment Failed</div>
              <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.6, marginBottom: 20 }}>{payError || "We could not complete your payment. Check your M-Pesa balance or try a different number."}</div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { setPayState(null); setPaying(false); }} style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1px solid ${c.border}`, background: "transparent", color: c.sub, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
                <button onClick={() => { setPayState(null); setPaying(false); handlePay(); }} style={{ flex: 2, padding: "13px 0", borderRadius: 12, border: "none", background: "#7c3aed", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Try Again</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const installments = myTicket?.installments || [];
  const amountPaid = myTicket?.amount_paid || 0;
  const totalDue = myTicket?.total_amount || 0;
  const remaining = totalDue - amountPaid;

  const completedInst = installments.filter(i => i.status === "completed");
  const nextInstNo = Math.min(3, completedInst.length + 1);
  const instAmount = nextInstNo >= 3 ? remaining : Math.max(0, Math.min(Math.ceil(totalDue / 3), remaining));

  const closeInst = () => { setInstOpen(false); setInstState(null); setInstError(""); setInstPaying(false); };

  const payNextInstallment = async () => {
    const ph = instPhone.replace(/\s/g, "");
    if (!/^(?:254|\+254|0)?7\d{8}$/.test(ph)) { flash("Enter a valid Safaricom number", "error"); return; }
    if (!myTicket?.id || instAmount <= 0) { flash("Nothing left to pay", "error"); return; }
    setInstPaying(true); setInstError(""); setInstState("waiting");
    const r = await api(`/event-tickets/${myTicket.id}/pay-installment`, { method: "POST", body: JSON.stringify({ phone: ph, amount: instAmount }) });
    if (!r.ok) { setInstError(r.data?.message || "Payment failed. Please try again."); setInstState("failed"); setInstPaying(false); return; }
    let attempts = 0;
    const poll = setInterval(async () => {
      attempts++;
      const s = await api(`/event-tickets/${myTicket.id}/status`);
      const paidNow = Number(s.data?.amount_paid || 0);
      if (s.ok && (s.data?.payment_status === "completed" || paidNow > Number(amountPaid))) {
        clearInterval(poll); setInstState("success"); setInstPaying(false);
        const fresh = await api(`/events/${ev.id}`);
        if (fresh.ok && fresh.data?.my_ticket) setMyTicket(fresh.data.my_ticket);
      } else if (s.ok && s.data?.payment_status === "cancelled") {
        clearInterval(poll); setInstError("Payment was cancelled."); setInstState("failed"); setInstPaying(false);
      } else if (attempts >= 24) {
        clearInterval(poll); setInstError("Payment timed out. Please try again."); setInstState("failed"); setInstPaying(false);
      }
    }, 5000);
  };

  return (
    <Shell title="Event Details" showBack onBack={onBack} noPad right={
      <button onClick={() => setShowShare(true)} aria-label="Share this event" style={{ width: 34, height: 34, borderRadius: 9, border: `1px solid ${c.border}`, background: c.card, color: c.sub, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
      </button>
    }>
      <div style={{ paddingBottom: 120 }}>

        {/* HERO — full width tall */}
        <div style={{ width: "100%", height: 260, background: ev.image_url ? `url(${ev.image_url}) center/cover no-repeat` : `linear-gradient(135deg,${PRI},${PRI2})`, position: "relative", marginBottom: 0 }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg,transparent 40%,rgba(0,0,0,0.72))" }} />
          {dl > 0 && !isPast && <div style={{ position: "absolute", top: 14, left: 14, background: "#ef4444", color: "#fff", fontSize: 10, fontWeight: 800, padding: "4px 10px", borderRadius: 20 }}>UPCOMING</div>}
          {dl > 0 && !isPast && <div style={{ position: "absolute", top: 14, right: 14, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: 20 }}>{dl} days left</div>}
          <div style={{ position: "absolute", bottom: 16, left: 16, right: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.75)", marginBottom: 4 }}>{fmtDate(ev.event_date)}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.2, marginBottom: 3 }}>{ev.title}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)" }}>📍 {ev.venue}{ev.location ? `, ${ev.location}` : ""}</div>
          </div>
        </div>

        <div style={{ padding: "0 16px" }}>

          {/* INFO SECTION */}
          <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 14, margin: "14px 0 10px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ fontSize: 18 }}>📅</span><span style={{ fontSize: 13, color: c.text }}>{fmtFullDate(ev.event_date)}</span></div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ fontSize: 18 }}>🕐</span><span style={{ fontSize: 13, color: c.text }}>{ev.event_time || "TBA"}{ev.time_end ? ` – ${ev.time_end}` : ""}</span></div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}><span style={{ fontSize: 18 }}>📍</span><span style={{ fontSize: 13, color: c.text }}>{ev.venue}{ev.location ? `, ${ev.location}` : ""}</span></div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ fontSize: 18 }}>👥</span><span style={{ fontSize: 13, color: c.text }}>{ev.tickets_count || 0} attending · Age {ev.age_min}–{ev.age_max}</span></div>
            </div>
          </div>

          {/* ABOUT */}
          {ev.description && (
            <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 14, marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 8 }}>About this event</div>
              <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.7 }}>
                {ev.description.split('\n').map((line, i) => (
                  line.trim() === '' 
                    ? <br key={i} /> 
                    : <p key={i} style={{ margin: "0 0 8px" }}>{line}</p>
                ))}
              </div>
            </div>
          )}

          {/* WHO'S COMING */}
          {ev.whos_coming && (
            <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 14, marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Who's coming</div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {[{ label: "Gays", color: PRI, pct: ev.whos_coming.gays || 0 }, { label: "Lesbians", color: "#f59e0b", pct: ev.whos_coming.lesbians || 0 }, { label: "Bisexuals", color: "#ec4899", pct: ev.whos_coming.bisexuals || 0 }, { label: "Straight", color: "#3b82f6", pct: ev.whos_coming.straight || 0 }, { label: "Others", color: "#9ca3af", pct: ev.whos_coming.others || 0 }].map(it => (
                  <div key={it.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 5, background: it.color }} />
                    <div style={{ fontSize: 10, fontWeight: 700, color: c.sub }}>{it.label}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: it.color }}>{it.pct}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* MY TICKET — installment progress */}
          {myTicket && myTicket.payment_status === "partial" && (
            <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 14, marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 10 }}>Your installment plan</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                {[1, 2, 3].map(n => {
                  const inst = installments.find(i => i.payment_number === n);
                  const done = inst?.status === "completed";
                  const pending = inst && !done;
                  return (
                    <div key={n} style={{ flex: 1, borderRadius: 10, border: `1px solid ${done ? GRN : c.border}`, background: done ? `${GRN}10` : c.input, padding: "8px 6px", textAlign: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: done ? GRN : PRI }}>{n}</div>
                      <div style={{ fontSize: 9, color: c.faint }}>Payment {n}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: done ? GRN : c.text }}>{inst ? `KES ${inst.amount}` : "—"}</div>
                      {done && <div style={{ fontSize: 9, color: GRN }}>✓ Paid</div>}
                      {pending && <div style={{ fontSize: 9, color: "#f59e0b" }}>Pending</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 12, color: c.sub, textAlign: "center" }}>KES {amountPaid} paid · KES {remaining} remaining</div>
            </div>
          )}

          {/* TICKETS SECTION */}
          {!isPast && !loadingDetail && (
            <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 14, marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Get tickets</div>

              {/* Ticket type selector */}
              {ticketTypes.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
                  {ticketTypes.map(tt => {
                    const remaining = tt.total_tickets - (tt.sold_count || 0);
                    const soldOut = remaining <= 0;
                    const selected = selectedType?.id === tt.id;
                    const accent = tierColors[tt.name] || PRI;
                    return (
                      <div key={tt.id} style={{ borderRadius: 12, border: selected ? `2px solid ${accent}` : `1px solid ${c.border}`, background: selected ? `${accent}10` : soldOut ? `${c.input}80` : c.bg, opacity: soldOut ? 0.5 : 1, overflow: "hidden", marginBottom: 0 }}>
                        <div onClick={() => !soldOut && setSelectedType(tt)} style={{ padding: "10px 12px", cursor: soldOut ? "not-allowed" : "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: selected ? accent : c.text }}>{tt.name}</div>
                            <div style={{ fontSize: 10, color: soldOut ? "#ef4444" : c.faint }}>{soldOut ? "Sold out" : `${remaining} spots left`}</div>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color: selected ? accent : c.text }}>KES {(tt.price || 0).toLocaleString()}</div>
                            {tt.benefits?.length > 0 && (
                              <button onClick={e => { e.stopPropagation(); setSelectedType(selected ? null : tt); }} style={{ fontSize: 10, color: accent, fontWeight: 700, background: "none", border: "none", cursor: "pointer", padding: "2px 6px", borderRadius: 6, background: `${accent}15` }}>▼ Benefits</button>
                            )}
                          </div>
                        </div>
                        {selected && tt.benefits?.length > 0 && (
                          <div style={{ padding: "0 12px 10px", borderTop: `1px solid ${c.border}` }}>
                            {tt.benefits.map((b, i) => (
                              <div key={i} style={{ fontSize: 11, color: c.sub, padding: "4px 0", display: "flex", gap: 8 }}>
                                <span style={{ color: accent }}>✓</span> {b}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: c.faint, marginBottom: 12, textAlign: "center" }}>Loading ticket types...</div>
              )}

              {/* Quantity */}
              {selectedType && (
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <span style={{ fontSize: 12, color: c.sub }}>Qty:</span>
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))} style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 18, cursor: "pointer" }}>−</button>
                  <span style={{ fontSize: 15, fontWeight: 700, color: c.text, minWidth: 20, textAlign: "center" }}>{quantity}</span>
                  <button onClick={() => setQuantity(q => Math.min(10, q + 1))} style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 18, cursor: "pointer" }}>+</button>
                  <span style={{ fontSize: 12, color: c.sub }}>= <strong style={{ color: c.text }}>KES {totalAmount.toLocaleString()}</strong></span>
                </div>
              )}

              {/* Payment tabs */}
              {selectedType && (
                <>
                  <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                    {[{ id: "mpesa", label: "📱 M-Pesa" }, { id: "tokens", label: "🪙 GT Tokens" }, ...(canInstallment ? [{ id: "install", label: "📅 Installments" }] : [])].map(t => (
                      <button key={t.id} onClick={() => setPayTab(t.id)} style={{ flex: 1, padding: "8px 4px", borderRadius: 10, border: `1.5px solid ${payTab === t.id ? PRI : c.border}`, background: payTab === t.id ? `${PRI}10` : c.bg, color: payTab === t.id ? PRI : c.sub, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>{t.label}</button>
                    ))}
                  </div>

                  {payTab === "mpesa" && (
                    <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="07XX XXX XXX" style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 15, fontFamily: "monospace", textAlign: "center", letterSpacing: 2, outline: "none", boxSizing: "border-box" }} />
                  )}

                  {payTab === "tokens" && (
                    <div style={{ background: `#f59e0b15`, border: `1px solid #f59e0b30`, borderRadius: 10, padding: "12px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: c.sub }}>Your balance</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: c.text }}>{user?.gtBalance || 0} GT</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 12, color: c.sub }}>Required</span>
                        <span style={{ fontSize: 16, fontWeight: 800, color: "#f59e0b" }}>{totalAmount} GT</span>
                      </div>
                      <div style={{ height: 1, background: `#f59e0b30`, marginBottom: 8 }} />
                      {(user?.gtBalance || 0) >= totalAmount ? (
                        <div style={{ fontSize: 12, color: "#10b981", fontWeight: 700, textAlign: "center" }}>✅ You have enough tokens — tap Pay below</div>
                      ) : (
                        <div style={{ fontSize: 12, color: "#ef4444", textAlign: "center" }}>⚠️ Need {totalAmount - (user?.gtBalance || 0)} more GT — top up your wallet</div>
                      )}
                    </div>
                  )}

                  {payTab === "install" && (
                    <>
                      <div style={{ background: "#f59e0b10", border: "1px solid #f59e0b30", borderRadius: 10, padding: "8px 12px", marginBottom: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b" }}>3 equal installments</div>
                        <div style={{ fontSize: 11, color: c.sub }}>Min deposit 30% = KES {minDeposit.toLocaleString()} · Final payment 3 days before event</div>
                      </div>
                      <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                        {[1, 2, 3].map(n => (
                          <div key={n} style={{ flex: 1, borderRadius: 10, border: `1px solid ${c.border}`, background: c.input, padding: "8px 6px", textAlign: "center" }}>
                            <div style={{ fontSize: 15, fontWeight: 800, color: PRI }}>{n}</div>
                            <div style={{ fontSize: 9, color: c.faint }}>Payment {n}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: c.text }}>KES {Math.ceil(totalAmount / 3).toLocaleString()}</div>
                          </div>
                        ))}
                      </div>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="07XX XXX XXX" style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 15, fontFamily: "monospace", textAlign: "center", letterSpacing: 2, outline: "none", boxSizing: "border-box" }} />
                    </>
                  )}

                  {/* PAY BUTTON inside section */}
                  <button onClick={handlePay} disabled={paying} style={{ width: "100%", marginTop: 14, padding: "15px 0", borderRadius: 14, border: "none", background: paying ? c.border : payTab === "tokens" ? `linear-gradient(135deg,#f59e0b,#d97706)` : `linear-gradient(135deg,${GRN},#059669)`, color: "#fff", fontSize: 15, fontWeight: 700, cursor: paying ? "not-allowed" : "pointer" }}>
                    {paying ? "⏳ Processing..." : payTab === "tokens" ? `🪙 Pay ${totalAmount} GT Tokens` : payTab === "install" ? `💰 Pay Installment 1 — KES ${Math.ceil(totalAmount/3).toLocaleString()}` : `💰 Pay KES ${totalAmount.toLocaleString()} via M-Pesa`}
                  </button>
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* STICKY BOTTOM BAR */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 16px", background: dark ? "#0b0c10ee" : "#ffffffee", backdropFilter: "blur(16px)", borderTop: `1px solid ${c.border}`, display: "flex", gap: 10, zIndex: 50 }}>
        {myTicket?.payment_status === "completed" ? (
          <button onClick={() => onViewTicket(myTicket)} style={{ flex: 1, padding: "15px 0", borderRadius: 14, border: "none", background: `linear-gradient(135deg,${PRI},${PRI2})`, color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>🎫 View My Ticket</button>
        ) : myTicket?.payment_status === "partial" ? (
          <button onClick={() => { setInstState(null); setInstError(""); setInstPhone(myTicket?.phone || ""); setInstOpen(true); }} style={{ flex: 1, padding: "15px 0", borderRadius: 14, border: "none", background: `linear-gradient(135deg,#f59e0b,#d97706)`, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>💰 Pay Installment {nextInstNo} — KES {instAmount.toLocaleString()}</button>
        ) : isPast ? (
          <button disabled style={{ flex: 1, padding: "15px 0", borderRadius: 14, border: "none", background: c.border, color: c.faint, fontSize: 15, fontWeight: 700 }}>Event Ended</button>
        ) : !selectedType ? (
          <button disabled style={{ flex: 1, padding: "15px 0", borderRadius: 14, border: "none", background: c.border, color: c.faint, fontSize: 14, fontWeight: 700 }}>Select a ticket type above</button>
        ) : (
          <button onClick={handlePay} disabled={paying} style={{ flex: 1, padding: "15px 0", borderRadius: 14, border: "none", background: paying ? c.border : `linear-gradient(135deg,${GRN},#059669)`, color: "#fff", fontSize: 15, fontWeight: 700, cursor: paying ? "not-allowed" : "pointer" }}>
            {paying ? "⏳ Processing..." : payTab === "tokens" ? `🪙 Pay ${totalAmount} GT` : payTab === "install" ? `💰 Deposit KES ${initialAmount || minDeposit}` : `💰 Pay KES ${totalAmount.toLocaleString()} via M-Pesa`}
          </button>
        )}
        <button onClick={() => setWishlist(w => !w)} style={{ width: 52, height: 52, borderRadius: 14, border: `1px solid ${c.border}`, background: c.card, fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: wishlist ? "#ef4444" : c.sub, flexShrink: 0 }}>{wishlist ? "❤️" : "♡"}</button>
        <button onClick={() => setShowShare(true)} style={{ width: 52, height: 52, borderRadius: 14, border: `1px solid ${c.border}`, background: c.card, fontSize: 22, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: c.sub, flexShrink: 0 }}>↗</button>
      </div>
    {showShare && <EventShareSheet ev={ev} onClose={() => setShowShare(false)} />}
    {instOpen && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ width: "100%", maxWidth: 340, background: c.card, borderRadius: 24, padding: 26, border: `1px solid ${c.border}` }}>
          {instState === null && (
            <>
              <div style={{ fontSize: 17, fontWeight: 800, color: c.text, marginBottom: 6, textAlign: "center" }}>Pay Installment {nextInstNo}</div>
              <div style={{ fontSize: 12, color: c.sub, textAlign: "center", marginBottom: 14 }}>KES {Number(amountPaid).toLocaleString()} paid of KES {Number(totalDue).toLocaleString()} · KES {Number(remaining).toLocaleString()} left</div>
              <div style={{ background: c.bg, borderRadius: 12, padding: "12px 14px", marginBottom: 14, textAlign: "center" }}>
                <div style={{ fontSize: 11, color: c.faint, marginBottom: 2 }}>Amount due now</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: PRI }}>KES {instAmount.toLocaleString()}</div>
              </div>
              <input type="tel" value={instPhone} onChange={e => setInstPhone(e.target.value)} placeholder="07XX XXX XXX" style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 15, fontFamily: "monospace", textAlign: "center", letterSpacing: 2, outline: "none", boxSizing: "border-box", marginBottom: 14 }} />
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={closeInst} style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1px solid ${c.border}`, background: "transparent", color: c.sub, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
                <button onClick={payNextInstallment} disabled={instPaying} style={{ flex: 2, padding: "13px 0", borderRadius: 12, border: "none", background: instPaying ? c.border : `linear-gradient(135deg,${GRN},#059669)`, color: "#fff", fontSize: 13, fontWeight: 700, cursor: instPaying ? "not-allowed" : "pointer" }}>Pay KES {instAmount.toLocaleString()}</button>
              </div>
            </>
          )}
          {instState === "waiting" && (
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 50, height: 50, border: "4px solid #e5e7eb", borderTopColor: "#10b981", borderRadius: "50%", animation: "gcSpin 1s linear infinite", margin: "0 auto 16px" }} />
              <style>{`@keyframes gcSpin{to{transform:rotate(360deg)}}`}</style>
              <div style={{ fontSize: 17, fontWeight: 800, color: c.text, marginBottom: 8 }}>Enter Your M-Pesa PIN</div>
              <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.6 }}>Check your phone for the prompt of KES {instAmount.toLocaleString()}. Do not close this page.</div>
            </div>
          )}
          {instState === "success" && (
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 70, height: 70, borderRadius: 35, background: "#e8fff0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 34, margin: "0 auto 16px" }}>🎉</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: c.text, marginBottom: 8 }}>Installment {nextInstNo} Paid!</div>
              <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.6, marginBottom: 18 }}>Your payment plan has been updated.</div>
              <button onClick={closeInst} style={{ width: "100%", padding: "13px 0", borderRadius: 12, border: "none", background: `linear-gradient(135deg,${PRI},${PRI2})`, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Done</button>
            </div>
          )}
          {instState === "failed" && (
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 70, height: 70, borderRadius: 35, background: "#fff0f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, margin: "0 auto 16px" }}>✕</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: c.text, marginBottom: 8 }}>Payment Failed</div>
              <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.6, marginBottom: 18 }}>{instError}</div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={closeInst} style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1px solid ${c.border}`, background: "transparent", color: c.sub, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Close</button>
                <button onClick={() => { setInstState(null); setInstError(""); }} style={{ flex: 2, padding: "13px 0", borderRadius: 12, border: "none", background: "#7c3aed", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Try Again</button>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
    <PayModal />
    </Shell>
  );
};

// ── TICKET DETAIL SCREEN ──────────────────────────────────────────────────────
const TicketDetail = ({ ticket, onBack }) => {
  const { c } = useApp();
  const ev = ticket.event || {};
  const tt = ticket.ticket_type || ticket.ticketType || {};
  const qty = ticket.quantity || 1;
  const orderDate = ticket.created_at ? new Date(ticket.created_at).toLocaleDateString("en", { month: "long", day: "numeric", year: "numeric" }) : "";
  const tickets = Array.from({ length: qty }, (_, i) => i + 1);
  return (
    <Shell title="Event Ticket" showBack onBack={onBack}>
      <div style={{ paddingBottom: 100 }}>
        {/* Purple header */}
        <div style={{ background: `linear-gradient(135deg, ${PRI}, ${PRI2})`, borderRadius: 16, padding: 16, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 14 }}>
            <div style={{ width: 90, height: 90, borderRadius: 12, background: ev.image_url ? `url(${ev.image_url}) center/cover` : "rgba(255,255,255,0.2)", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.7)", marginBottom: 4 }}>{ev.event_date ? fmtDate(ev.event_date) : ""}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", marginBottom: 6 }}>{ev.title}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", marginBottom: 2 }}>📍 {ev.venue}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", marginBottom: 10 }}>🕐 {ev.event_time || "TBA"}{ev.time_end ? ` – ${ev.time_end}` : ""}</div>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, background: `${GRN}20`, border: `1px solid ${GRN}` }}>
                <span style={{ fontSize: 11, color: GRN, fontWeight: 700 }}>✅ Valid Ticket</span>
              </div>
            </div>
          </div>
        </div>
        {/* Order info */}
        <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: 16, marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 10, color: c.sub, fontWeight: 600, marginBottom: 4 }}>Ticket Type</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.text }}>{tt.name || "General Admission"}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, color: c.sub, fontWeight: 600, marginBottom: 4 }}>Quantity</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.text }}>{qty}</div>
            </div>
          </div>
          <div style={{ height: 1, background: c.border, marginBottom: 14 }} />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 10, color: c.sub, fontWeight: 600, marginBottom: 4 }}>Order ID</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.text, fontFamily: "monospace" }}>#{ticket.ticket_number}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, color: c.sub, fontWeight: 600, marginBottom: 4 }}>Purchase Date</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{orderDate}</div>
            </div>
          </div>
        </div>
        {/* Individual tickets with QR */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 10 }}>Tickets</div>
          {tickets.map(i => (
            <div key={i} style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: "12px 16px", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: c.priBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🎫</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Ticket {i}</div>
                  <div style={{ fontSize: 11, color: c.sub }}>Admit One</div>
                </div>
              </div>
              {/* QR code placeholder */}
              <div style={{ width: 52, height: 52, borderRadius: 8, background: c.input, border: `1px solid ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24 }}>⊞</div>
            </div>
          ))}
        </div>
        {/* Important info */}
        <div style={{ background: c.priBg, borderRadius: 14, border: `1px solid ${c.priBrd}`, padding: 14, marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 16 }}>ℹ️</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: c.text }}>Important Information</span>
          </div>
          {["Show this ticket at the entrance.", "Non-refundable.", "18+ Event. Please bring a valid ID."].map(info => (
            <div key={info} style={{ fontSize: 12, color: c.sub, marginBottom: 4, paddingLeft: 8 }}>• {info}</div>
          ))}
        </div>
      </div>
      {/* Add to Wallet button */}
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 16px", background: c.bg, borderTop: `1px solid ${c.border}`, zIndex: 50 }}>
        <button style={{ width: "100%", padding: "15px 0", borderRadius: 14, border: "none", background: `linear-gradient(135deg, ${PRI}, ${PRI2})`, color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
          🎫 Add to Wallet
        </button>
      </div>
    </Shell>
  );
};

// ── BUY TICKET SCREEN ─────────────────────────────────────────────────────────
const BuyScreen = ({ ev, onBack, onSuccess, userGtBalance }) => {
  const { c, flash } = useApp();
  const [selectedType, setSelectedType] = useState(null);
  const [expandedBenefits, setExpandedBenefits] = useState({});
  const [payMethod, setPayMethod] = useState("mpesa");
  const [phone, setPhone] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [initialAmount, setInitialAmount] = useState("");
  const [paying, setPaying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [paidMethod, setPaidMethod] = useState("");
  const [payState, setPayState] = useState(null); // null | "processing" | "success" | "failed"
  const [payError, setPayError] = useState("");

  const totalAmount = selectedType ? selectedType.price * quantity : 0;
  const minDeposit = selectedType ? Math.ceil(totalAmount * 0.30) : 0;
  const dl = daysLeft(ev.event_date);
  const canInstallment = dl >= 3;

  const tierColors = { "Early Bird": GRN, "Regular": PRI, "VIP": "#f59e0b", "VVIP": "#ef4444" };

  const handlePay = async () => {
    if (!selectedType) { flash("Please select a ticket type", "error"); return; }
    setPayState("processing");
    setPaying(true);
    try {
      if (payMethod === "mpesa") {
        if (!phone || phone.length < 9) { flash("Enter a valid phone number", "error"); setPaying(false); return; }
        const r = await api(`/events/${ev.id}/buy-ticket`, { method: "POST", body: JSON.stringify({ ticket_type_id: selectedType.id, phone, quantity }) });
        if (r.ok) { setPaidMethod("mpesa"); setPayState("success"); setPaid(true); }
        else { setPayError(r.data?.message || "Payment failed. Check your M-Pesa balance or try a different number."); setPayState("failed"); }
      } else if (payMethod === "tokens") {
        const r = await api(`/events/${ev.id}/buy-with-tokens`, { method: "POST", body: JSON.stringify({ ticket_type_id: selectedType.id, quantity }) });
        if (r.ok) { setPaidMethod("tokens"); setPayState("success"); setPaid(true); }
        else { setPayError(r.data?.message || "Insufficient GT Tokens. Please top up your wallet."); setPayState("failed"); }
      } else if (payMethod === "installment") {
        if (!phone || phone.length < 9) { setPayState(null); setPaying(false); flash("Enter a valid phone number", "error"); return; }
        const amt = Math.ceil(totalAmount / 3);
        const r = await api(`/events/${ev.id}/start-installment`, { method: "POST", body: JSON.stringify({ ticket_type_id: selectedType.id, phone, quantity: parseInt(quantity), initial_amount: amt }) });
        if (r.ok) { setPaidMethod("installment"); setPayState("success"); setPaid(true); }
        else { setPayError(r.data?.message || "Payment failed. Please try again."); setPayState("failed"); }
      }
    } finally { setPaying(false); }
  };

        const PayModal = () => {
    if (!payState) return null;
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ width: "100%", maxWidth: 340, background: c.card, borderRadius: 24, padding: 30, textAlign: "center", border: `1px solid ${c.border}` }}>
          {payState === "processing" && (
            <>
              <div style={{ width: 80, height: 80, borderRadius: 40, background: "#e8fff0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, margin: "0 auto 20px" }}>📱</div>
              <div style={{ width: 50, height: 50, border: "4px solid #e5e7eb", borderTopColor: "#10b981", borderRadius: "50%", animation: "gcSpin 1s linear infinite", margin: "0 auto 16px" }} />
              <style>{`@keyframes gcSpin{to{transform:rotate(360deg)}}`}</style>
              <div style={{ fontSize: 18, fontWeight: 800, color: c.text, marginBottom: 8 }}>Processing Payment</div>
              <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.6 }}>
                {payMethod === "tokens" ? "Deducting GT Tokens..." : "Sending M-Pesa prompt to your phone..."}
              </div>
            </>
          )}
          {payState === "success" && (
            <>
              <div style={{ width: 80, height: 80, borderRadius: 40, background: "#e8fff0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, margin: "0 auto 20px" }}>🎉</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: c.text, marginBottom: 8 }}>
                {paidMethod === "tokens" ? "Tickets Confirmed!" : paidMethod === "installment" ? "Deposit Initiated!" : "M-Pesa Prompt Sent!"}
              </div>
              <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.6, marginBottom: 20 }}>
                {paidMethod === "tokens" && "Your GT Tokens have been deducted. Ticket confirmed!"}
                {paidMethod === "installment" && `Check your phone for the M-Pesa prompt. Payment 1 of 3 — KES ${Math.ceil(totalAmount / 3).toLocaleString()}.`}
                {paidMethod === "mpesa" && "Check your phone for the M-Pesa STK push. Enter your PIN to complete booking."}
              </div>
              <div style={{ background: c.bg, borderRadius: 12, padding: "12px 16px", marginBottom: 20, textAlign: "left" }}>
                <div style={{ fontSize: 11, color: c.faint, marginBottom: 2 }}>Event</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 6 }}>{ev.title}</div>
                <div style={{ fontSize: 11, color: c.faint, marginBottom: 2 }}>Ticket</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 6 }}>{selectedType?.name} × {quantity}</div>
                <div style={{ fontSize: 11, color: c.faint, marginBottom: 2 }}>Amount</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: PRI }}>KES {totalAmount.toLocaleString()}</div>
              </div>
              <button onClick={() => { setPayState(null); onSuccess(); }} style={{ width: "100%", padding: "14px 0", borderRadius: 14, border: "none", background: `linear-gradient(135deg,${PRI},${PRI2})`, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>🎫 View My Tickets</button>
            </>
          )}
          {payState === "failed" && (
            <>
              <div style={{ width: 80, height: 80, borderRadius: 40, background: "#fff0f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, margin: "0 auto 20px" }}>✕</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: c.text, marginBottom: 8 }}>Payment Failed</div>
              <div style={{ fontSize: 13, color: c.sub, lineHeight: 1.6, marginBottom: 20 }}>{payError || "We could not complete your payment. Check your M-Pesa balance or try a different number."}</div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => { setPayState(null); setPaying(false); }} style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1px solid ${c.border}`, background: "transparent", color: c.sub, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
                <button onClick={() => { setPayState(null); setPaying(false); handlePay(); }} style={{ flex: 2, padding: "13px 0", borderRadius: 12, border: "none", background: "#7c3aed", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Try Again</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  if (paid) return (
    <Shell title="Booking Confirmed" showBack onBack={onSuccess}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", padding: "0 24px", textAlign: "center" }}>
        <div style={{ width: 80, height: 80, borderRadius: 40, background: paidMethod === "tokens" ? "#f59e0b20" : "#10b98120", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 40, marginBottom: 20 }}>
          {paidMethod === "tokens" ? "🪙" : paidMethod === "installment" ? "📅" : "📱"}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: c.text, marginBottom: 10 }}>
          {paidMethod === "tokens" ? "Tickets Confirmed! 🎉" : paidMethod === "installment" ? "Deposit Initiated! 📅" : "M-Pesa Prompt Sent! 📱"}
        </div>
        <div style={{ fontSize: 14, color: c.sub, lineHeight: 1.7, marginBottom: 10 }}>
          {paidMethod === "tokens" && "Your GT Tokens have been deducted. Your ticket is confirmed!"}
          {paidMethod === "installment" && `Check your phone for the M-Pesa prompt. Payment 1 of 3 — KES ${Math.ceil(totalAmount / 3).toLocaleString()}.`}
          {paidMethod === "mpesa" && "Check your phone for the M-Pesa STK push prompt. Once paid your ticket will be confirmed."}
        </div>
        <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16, width: "100%", marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: c.sub, marginBottom: 4 }}>Event</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: c.text, marginBottom: 8 }}>{ev.title}</div>
          <div style={{ fontSize: 12, color: c.sub, marginBottom: 4 }}>Ticket</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 8 }}>{selectedType?.name} × {quantity}</div>
          <div style={{ fontSize: 12, color: c.sub, marginBottom: 4 }}>Total</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: PRI }}>KES {totalAmount.toLocaleString()}</div>
        </div>
        <button onClick={onSuccess} style={{ width: "100%", padding: "15px 0", borderRadius: 14, border: "none", background: `linear-gradient(135deg,${PRI},${PRI2})`, color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
          🎫 View My Tickets
        </button>
      </div>
    </Shell>
  );

  return (
    <Shell title="Get Tickets" showBack onBack={onBack}>
      <div style={{ paddingBottom: 100 }}>
        {/* Event mini header */}
        <div style={{ background: ev.image_url ? `url(${ev.image_url}) center/cover` : `linear-gradient(135deg, ${PRI}, ${PRI2})`, height: 120, borderRadius: 16, marginBottom: 14, position: "relative" }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.7))", borderRadius: 16 }} />
          <div style={{ position: "absolute", bottom: 12, left: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>{ev.title}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>📍 {ev.venue} · {ev.event_time || "TBA"}</div>
          </div>
        </div>

        {/* Ticket types */}
        <div style={{ fontSize: 14, fontWeight: 800, color: c.text, marginBottom: 10 }}>🎫 Choose Your Ticket</div>
        {(ev.ticket_types || ev.ticketTypes || []).map(tt => {
          const remaining = tt.total_tickets - (tt.sold_count || 0);
          const soldOut = remaining <= 0;
          const selected = selectedType?.id === tt.id;
          const accent = tierColors[tt.name] || PRI;
          const expanded = expandedBenefits[tt.id];
          return (
            <div key={tt.id} style={{ borderRadius: 16, border: selected ? `2px solid ${accent}` : `1.5px solid ${c.border}`, background: selected ? `${accent}08` : soldOut ? `${c.input}60` : c.card, marginBottom: 8, overflow: "hidden", opacity: soldOut ? 0.5 : 1 }}>
              <div onClick={() => !soldOut && setSelectedType(tt)} style={{ padding: 16, cursor: soldOut ? "not-allowed" : "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 22, height: 22, borderRadius: 11, border: `2px solid ${selected ? accent : c.border}`, background: selected ? accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {selected && <svg width="11" height="11" viewBox="0 0 24 24"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" fill="white" /></svg>}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: selected ? accent : c.text }}>{tt.name}</div>
                      <div style={{ fontSize: 11, color: soldOut ? "#ef4444" : c.faint, fontWeight: 600 }}>{soldOut ? "SOLD OUT" : `${remaining} spots left`}</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: selected ? accent : c.text }}>KES {tt.price.toLocaleString()}</div>
                    {tt.benefits?.length > 0 && (
                      <button onClick={e => { e.stopPropagation(); setExpandedBenefits(p => ({ ...p, [tt.id]: !p[tt.id] })); }}
                        style={{ fontSize: 10, color: accent, fontWeight: 700, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                        {expanded ? "▲ Hide" : "▼ Benefits"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
              {/* Benefits dropdown */}
              {expanded && tt.benefits?.length > 0 && (
                <div style={{ padding: "0 16px 14px", borderTop: `1px solid ${c.border}` }}>
                  {tt.benefits.map((b, i) => (
                    <div key={i} style={{ fontSize: 12, color: c.sub, padding: "4px 0", display: "flex", gap: 8 }}>
                      <span style={{ color: accent }}>✓</span> {b}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Quantity */}
        {selectedType && (
          <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 10 }}>Quantity</div>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <button onClick={() => setQuantity(q => Math.max(1, q - 1))} style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${c.border}`, background: c.input, color: c.text, fontSize: 20, cursor: "pointer" }}>−</button>
              <span style={{ fontSize: 18, fontWeight: 700, color: c.text, minWidth: 24, textAlign: "center" }}>{quantity}</span>
              <button onClick={() => setQuantity(q => Math.min(10, q + 1))} style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${c.border}`, background: c.input, color: c.text, fontSize: 20, cursor: "pointer" }}>+</button>
              <span style={{ fontSize: 13, color: c.sub }}>× KES {selectedType.price.toLocaleString()} = <strong style={{ color: c.text }}>KES {totalAmount.toLocaleString()}</strong></span>
            </div>
          </div>
        )}

        {/* Payment method */}
        {selectedType && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 10 }}>Payment Method</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              {[
                { id: "mpesa", label: "M-Pesa", icon: "📱" },
                { id: "tokens", label: `GT Tokens (${userGtBalance || 0})`, icon: "🪙" },
                ...(canInstallment ? [{ id: "installment", label: "Installments", icon: "📅" }] : []),
              ].map(m => (
                <button key={m.id} onClick={() => setPayMethod(m.id)}
                  style={{ flex: 1, padding: "10px 6px", borderRadius: 12, border: `1.5px solid ${payMethod === m.id ? PRI : c.border}`, background: payMethod === m.id ? c.priBg : c.card, color: payMethod === m.id ? PRI : c.sub, fontSize: 11, fontWeight: 700, cursor: "pointer", textAlign: "center" }}>
                  <div style={{ fontSize: 18, marginBottom: 3 }}>{m.icon}</div>
                  {m.label}
                </button>
              ))}
            </div>

            {/* Phone input for mpesa / installment */}
            {(payMethod === "mpesa" || payMethod === "installment") && (
              <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: c.text, marginBottom: 8 }}>📱 Safaricom Number</div>
                <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="07XX XXX XXX"
                  style={{ width: "100%", padding: "14px 16px", borderRadius: 12, border: `1.5px solid ${c.border}`, background: c.input, color: c.text, fontSize: 16, fontWeight: 700, outline: "none", boxSizing: "border-box", textAlign: "center", letterSpacing: 2, fontFamily: "monospace" }} />
              </div>
            )}

            {/* Installment amount input */}
            {payMethod === "installment" && (
              <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: c.text, marginBottom: 4 }}>Initial Deposit Amount</div>
                <div style={{ fontSize: 11, color: c.sub, marginBottom: 8 }}>Minimum 30% = KES {minDeposit.toLocaleString()} · Up to 3 payments · Final payment due 3 days before event</div>
                <input type="number" value={initialAmount} onChange={e => setInitialAmount(e.target.value)} placeholder={`Min KES ${minDeposit}`}
                  style={{ width: "100%", padding: "14px 16px", borderRadius: 12, border: `1.5px solid ${c.border}`, background: c.input, color: c.text, fontSize: 16, fontWeight: 700, outline: "none", boxSizing: "border-box", textAlign: "center", fontFamily: "monospace" }} />
              </div>
            )}

            {/* GT Tokens warning */}
            {payMethod === "tokens" && (
              <div style={{ background: c.goldBg || "#f59e0b12", borderRadius: 12, padding: 12, marginBottom: 12, border: `1px solid #f59e0b30` }}>
                <div style={{ fontSize: 12, color: c.text }}>🪙 Your balance: <strong>{userGtBalance || 0} GT</strong> · Required: <strong>{totalAmount} GT</strong></div>
                {(userGtBalance || 0) < totalAmount && <div style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>⚠️ Insufficient GT Tokens</div>}
              </div>
            )}

            {/* PAY BUTTON */}
            <button onClick={handlePay} disabled={paying} style={{ width: "100%", marginTop: 8, padding: "16px 0", borderRadius: 14, border: "none", background: paying ? c.border : payMethod === "tokens" ? `linear-gradient(135deg,#f59e0b,#d97706)` : `linear-gradient(135deg,${GRN},#059669)`, color: "#fff", fontSize: 15, fontWeight: 800, cursor: paying ? "not-allowed" : "pointer" }}>
              {paying ? "⏳ Processing..." : payMethod === "tokens" ? `🪙 Pay ${totalAmount} GT Tokens` : payMethod === "installment" ? `💰 Pay Deposit KES ${Math.ceil(totalAmount/3).toLocaleString()} via M-Pesa` : `💰 Pay KES ${totalAmount.toLocaleString()} via M-Pesa`}
            </button>
            <div style={{ textAlign: "center", marginTop: 6, fontSize: 10, color: c.faint }}>🔒 Secured payment</div>
          </>
        )}
      </div>
    <PayModal />
    </Shell>
  );
};

// ── MAIN EVENTS PAGE ──────────────────────────────────────────────────────────
export const EventsPage = () => {
  const { c, user, flash } = useApp();
  const [tab, setTab] = useState("upcoming");
  const [screen, setScreen] = useState("list"); // list | detail | buy | ticket
  const [events, setEvents] = useState({ upcoming: [], past: [] });
  const [myTickets, setMyTickets] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [r, t] = await Promise.all([api("/events"), api("/my-tickets")]);
    if (r.ok) setEvents(r.data);
    if (t.ok) setMyTickets(t.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // ── DEEP LINK: /event/{id} or ?event={id} → open that event's detail ──
  useEffect(() => {
    const dl = peekDeepLink();
    if (!dl || dl.type !== "event") return;
    (async () => {
      const r = await api(`/events/${dl.id}`);
      const fresh = r.ok && r.data ? (r.data.event || r.data) : null;
      consumeDeepLink();
      if (fresh?.id) { setSelectedEvent(fresh); setScreen("detail"); }
      else flash("That event is no longer available", "error");
    })();
  }, []); // eslint-disable-line

  const getMyTicket = (eventId) => myTickets.find(t => t.event_id === eventId && ["completed", "partial"].includes(t.payment_status));

  const goDetail = (ev) => { setSelectedEvent(ev); setScreen("detail"); };
  const goMyTickets = () => { load(); setScreen("list"); setTab("mytickets"); setSelectedEvent(null); };
  const goBuy = (ev) => { setSelectedEvent(ev); setScreen("buy"); };
  const goTicket = (ticket) => { setSelectedTicket(ticket); setScreen("ticket"); };
  const goBack = () => { setScreen("list"); setSelectedEvent(null); setSelectedTicket(null); };

  // Sub-screens
  if (screen === "detail" && selectedEvent)
    return <EventDetail ev={selectedEvent} myTicket={getMyTicket(selectedEvent.id)} onBack={goBack} onBuy={goBuy} onViewTicket={goTicket} onMyTickets={goMyTickets} />;
  if (screen === "buy" && selectedEvent)
    return <BuyScreen ev={selectedEvent} onBack={() => setScreen("detail")} onSuccess={() => { load(); setScreen("list"); }} userGtBalance={user?.gtBalance} />;
  if (screen === "ticket" && selectedTicket)
    return <TicketDetail ticket={selectedTicket} onBack={goBack} />;

  // Main list
  return (
    <Shell title="Events">
      <div style={{ paddingBottom: 20 }}>
        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[{ id: "upcoming", label: "Upcoming", icon: "🎉" }, { id: "mytickets", label: "My Tickets", icon: "🎫" }, { id: "past", label: "Past Events", icon: "📷" }].map(tb => (
            <button key={tb.id} onClick={() => setTab(tb.id)}
              style={{ flex: 1, padding: "10px 4px", borderRadius: 24, border: `1.5px solid ${tab === tb.id ? PRI : c.border}`, background: tab === tb.id ? c.priBg : c.card, color: tab === tb.id ? PRI : c.sub, fontSize: 11, fontWeight: 700, cursor: "pointer", textAlign: "center" }}>
              {tb.icon} {tb.label}
            </button>
          ))}
        </div>

        {/* Section heading */}
        {tab === "upcoming" && <><div style={{ fontSize: 20, fontWeight: 800, color: c.text, marginBottom: 4 }}>Upcoming Events</div><div style={{ fontSize: 13, color: c.sub, marginBottom: 16 }}>Discover exciting events and see who's attending!</div></>}
        {tab === "mytickets" && <><div style={{ fontSize: 20, fontWeight: 800, color: c.text, marginBottom: 4 }}>My Tickets</div><div style={{ fontSize: 13, color: c.sub, marginBottom: 16 }}>Your upcoming events and tickets in one place.</div></>}
        {tab === "past" && <><div style={{ fontSize: 20, fontWeight: 800, color: c.text, marginBottom: 4 }}>Past Events</div><div style={{ fontSize: 13, color: c.sub, marginBottom: 16 }}>Events you've attended.</div></>}

        {loading && <div style={{ textAlign: "center", padding: 40, color: c.sub, fontSize: 14 }}>Loading events...</div>}

        {!loading && tab === "upcoming" && (
          events.upcoming?.length > 0
            ? events.upcoming.map(ev => <UpcomingCard key={ev.id} ev={ev} myTicket={getMyTicket(ev.id)} onViewDetail={goDetail} />)
            : <EmptyState icon="🎉" title="No upcoming events" desc="Stay tuned — exciting events are coming soon!" />
        )}

        {!loading && tab === "mytickets" && (
          myTickets.length > 0
            ? myTickets.map(t => <MyTicketCard key={t.id} ticket={t} onView={goTicket} />)
            : <EmptyState icon="🎫" title="No tickets yet" desc="Browse upcoming events and grab your tickets!" />
        )}

        {!loading && tab === "past" && (
          events.past?.length > 0
            ? events.past.map(ev => <PastCard key={ev.id} ev={ev} attended={!!getMyTicket(ev.id)} onViewDetail={goDetail} />)
            : <EmptyState icon="📷" title="No past events" desc="Events history will appear here" />
        )}
      </div>
    </Shell>
  );
};